import type { WebWarmupConfig } from "@storyboard/web-client";
import { getWorkspaceConfig } from "@/data/workspaceConfig";

/**
 * Whether warmup runs, and which feeds it reads how.
 *
 * `off` skips it. `limited` reads up to `feedLimit` feeds, unread first and then the busiest, plus every driver
 * channel on a Dispatch workspace, in batches. `all` reads every feed the member can read, in batches. `workspace`
 * reads every readable feed in one walk with no feed list; experimental, since that read costs the fleet's items.
 */
export type WarmupMode = NonNullable<WebWarmupConfig["mode"]>;

/** A workspace's `web.warmup` with every field filled in. */
export type WarmupSettings = {
  mode: WarmupMode;
  /** How many feeds `limited` picks, before the driver channels Dispatch adds. */
  feedLimit: number;
  /** How many feeds one event-log read names under `limited` and `all`. */
  feedsPerBatch: number;
  /** How far back the walk holds the workspace's items, in hours. */
  lookbackHours: number;
};

/**
 * The settings a workspace without a stored `web.warmup` runs with. Equal to the server's `DefaultWorkspaceConfig`, so
 * a client that reads a config compiled before the block existed behaves the same as one that reads the defaults.
 */
export const DEFAULT_WARMUP_SETTINGS: WarmupSettings = {
  mode: "limited",
  feedLimit: 50,
  feedsPerBatch: 100,
  lookbackHours: 48,
};

/** A stored `web.warmup`, each missing field taken from {@link DEFAULT_WARMUP_SETTINGS}. */
export function resolveWarmupSettings(stored: WebWarmupConfig | undefined): WarmupSettings {
  return {
    mode: stored?.mode ?? DEFAULT_WARMUP_SETTINGS.mode,
    feedLimit: stored?.feedLimit ?? DEFAULT_WARMUP_SETTINGS.feedLimit,
    feedsPerBatch: stored?.feedsPerBatch ?? DEFAULT_WARMUP_SETTINGS.feedsPerBatch,
    lookbackHours: stored?.lookbackHours ?? DEFAULT_WARMUP_SETTINGS.lookbackHours,
  };
}

/**
 * A workspace's compiled `web.warmup`, resolved by {@link resolveWarmupSettings}.
 *
 * Kept apart from the warmup itself so a sync target can read the mode without the action graph.
 */
export function warmupSettingsFor(workspaceId: string): WarmupSettings {
  return resolveWarmupSettings(getWorkspaceConfig(workspaceId)?.web?.warmup);
}
